/**
 * Enova Cross-sell Carousel
 * Carrusel con skeleton solo en inestabilidad visual
 */

class EnovaCrosssellCarousel {
  constructor() {
    this.currentIndex = 0;
    this.isUpdating = false;
    this.isInitialized = false;
    this.cartProductIds = [];
    this.lastVisibleCount = 0; // Para detectar cambios en slides visibles
    this.slideWidth = 0;
    this.config = {
      mobileColumns: parseFloat(window.enovaCrosssellConfig?.mobileColumns || 1),
      desktopColumns: parseFloat(window.enovaCrosssellConfig?.desktopColumns || 1.25),
      spaceBetween: parseInt(window.enovaCrosssellConfig?.spaceBetween || 10),
      breakpoint: 750
    };
    
    this.init();
  }

  getColumns() {
    return window.innerWidth < this.config.breakpoint 
      ? this.config.mobileColumns 
      : this.config.desktopColumns;
  }

  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    console.log('🎯 Enova Cross-sell setup');
    this.bindEvents();
    this.observeAll();
    this.initialLoad();
  }

  async initialLoad() {
    if (this.isInitialized) return;
    this.isInitialized = true;
    
    const carousel = document.getElementById('enova-crosssell-carousel');
    if (!carousel) return;

    console.log('🚀 Initial load...');
    await this.syncWithCart();
    this.lastVisibleCount = this.getVisibleSlides().length;
    console.log('✅ Ready');
  }

  // ==========================================
  // SKELETON - Solo en inestabilidad visual
  // ==========================================

  showSkeleton() {
    const carousel = document.getElementById('enova-crosssell-carousel');
    if (carousel && !carousel.classList.contains('is-loading')) {
      carousel.classList.add('is-loading');
      console.log('⏳ Skeleton ON (layout change)');
    }
  }

  hideSkeleton() {
    const carousel = document.getElementById('enova-crosssell-carousel');
    if (carousel) {
      carousel.classList.remove('is-loading');
      console.log('✅ Skeleton OFF');
    }
  }

  // ==========================================
  // SINCRONIZACIÓN CON CARRITO
  // ==========================================

  async syncWithCart() {
    const carousel = document.getElementById('enova-crosssell-carousel');
    if (!carousel) return;

    // Obtener IDs actuales del carrito
    const newIds = await this.fetchCartProductIds();
    
    // Detectar si habrá cambio en slides visibles
    const currentVisible = this.getVisibleSlides().length;
    const willChange = this.willLayoutChange(newIds);
    
    // Si va a cambiar el layout, mostrar skeleton
    if (willChange && this.isInitialized && currentVisible > 0) {
      this.showSkeleton();
    }
    
    // Actualizar estado
    this.cartProductIds = newIds;
    
    // Aplicar cambios
    this.applyFilter();
    this.calculateLayout();
    this.goToSlide(0, false);
    this.updateNavigation();
    
    // Actualizar conteo y ocultar skeleton
    const newVisible = this.getVisibleSlides().length;
    if (newVisible !== this.lastVisibleCount) {
      this.lastVisibleCount = newVisible;
    }
    
    // Ocultar skeleton después de que el layout esté estable
    if (willChange) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.hideSkeleton();
        });
      });
    }
  }

  willLayoutChange(newCartIds) {
    // Verificar si algún slide visible pasará a estar oculto o viceversa
    const carousel = document.getElementById('enova-crosssell-carousel');
    if (!carousel) return false;

    const slides = carousel.querySelectorAll('.enova-crosssell__slide');
    let changeDetected = false;

    slides.forEach(slide => {
      const item = slide.querySelector('.enova-crosssell-item');
      if (!item) return;

      const productId = parseInt(item.dataset.productId, 10);
      const isCurrentlyHidden = slide.hasAttribute('data-hidden');
      const willBeHidden = newCartIds.includes(productId);

      // Detectar cambio de estado
      if (isCurrentlyHidden !== willBeHidden) {
        changeDetected = true;
      }
    });

    return changeDetected;
  }

  async fetchCartProductIds() {
    try {
      const timestamp = Date.now();
      const response = await fetch(`${window.Shopify.routes.root}cart.js?t=${timestamp}`);
      const cart = await response.json();
      const ids = [...new Set(cart.items.map(item => item.product_id))];
      console.log('🛒 Cart IDs:', ids);
      return ids;
    } catch (e) {
      console.error('❌ Error fetching cart:', e);
      return this.cartProductIds;
    }
  }

  refresh() {
    this.calculateLayout();
    this.goToSlide(this.currentIndex, false);
    this.updateNavigation();
  }

  // ==========================================
  // FILTRADO
  // ==========================================

  applyFilter() {
    const carousel = document.getElementById('enova-crosssell-carousel');
    if (!carousel) return;

    const slides = carousel.querySelectorAll('.enova-crosssell__slide');
    let hiddenCount = 0;
    let visibleCount = 0;

    slides.forEach(slide => {
      const item = slide.querySelector('.enova-crosssell-item');
      if (!item) return;

      const productId = parseInt(item.dataset.productId, 10);
      const isInCart = this.cartProductIds.includes(productId);

      // Usar clase para ocultar, NO display:none
      if (isInCart) {
        slide.setAttribute('data-hidden', 'true');
        hiddenCount++;
      } else {
        slide.removeAttribute('data-hidden');
        visibleCount++;
      }
    });

    console.log(`📊 Filter: ${hiddenCount} hidden, ${visibleCount} visible`);

    // Mostrar/ocultar carrusel completo
    const cartDrawer = document.querySelector('cart-drawer');
    const isEmpty = cartDrawer?.classList.contains('is-empty') || visibleCount === 0;
    carousel.style.display = isEmpty ? 'none' : '';
  }

  getVisibleSlides() {
    const carousel = document.getElementById('enova-crosssell-carousel');
    if (!carousel) return [];
    
    return Array.from(carousel.querySelectorAll('.enova-crosssell__slide:not([data-hidden])'));
  }

  // ==========================================
  // LAYOUT ESTABLE
  // ==========================================

  calculateLayout() {
    const carousel = document.getElementById('enova-crosssell-carousel');
    if (!carousel) return;

    const viewport = carousel.querySelector('.enova-crosssell__viewport');
    const track = carousel.querySelector('.enova-crosssell__track');
    const allSlides = carousel.querySelectorAll('.enova-crosssell__slide');
    
    if (!viewport || !track) return;

    const viewportWidth = viewport.offsetWidth;
    const gap = this.config.spaceBetween;
    const cols = this.getColumns();
    
    // Calcular ancho fijo para cada slide
    this.slideWidth = (viewportWidth - (gap * (Math.ceil(cols) - 1))) / cols;

    // Aplicar estilos a TODOS los slides
    allSlides.forEach(slide => {
      slide.style.width = `${this.slideWidth}px`;
      slide.style.minWidth = `${this.slideWidth}px`;
      slide.style.maxWidth = `${this.slideWidth}px`;
      slide.style.flexShrink = '0';
      
      // Ocultar con visibility para mantener espacio
      if (slide.hasAttribute('data-hidden')) {
        slide.style.display = 'none';
      } else {
        slide.style.display = '';
      }
    });

    track.style.display = 'flex';
    track.style.gap = `${gap}px`;

    console.log(`📐 Layout: slideWidth=${this.slideWidth}px, gap=${gap}px`);
  }

  // ==========================================
  // NAVEGACIÓN
  // ==========================================

  goToSlide(index, animate = true) {
    const carousel = document.getElementById('enova-crosssell-carousel');
    if (!carousel) return;

    const track = carousel.querySelector('.enova-crosssell__track');
    const visibleSlides = this.getVisibleSlides();
    
    if (!track || visibleSlides.length === 0) return;

    const gap = this.config.spaceBetween;
    const cols = this.getColumns();
    
    // Calcular máximo índice
    const maxIndex = Math.max(0, visibleSlides.length - Math.floor(cols));
    this.currentIndex = Math.max(0, Math.min(index, maxIndex));
    
    // Calcular offset basado en slides visibles
    const offset = this.currentIndex * (this.slideWidth + gap);
    
    track.style.transition = animate ? 'transform 0.3s ease' : 'none';
    track.style.transform = `translateX(-${offset}px)`;
    
    this.updateNavigation();
  }

  updateNavigation() {
    const carousel = document.getElementById('enova-crosssell-carousel');
    if (!carousel) return;

    const prevBtn = carousel.querySelector('.enova-crosssell-nav__btn--prev');
    const nextBtn = carousel.querySelector('.enova-crosssell-nav__btn--next');
    const visibleSlides = this.getVisibleSlides();
    
    if (!prevBtn || !nextBtn) return;

    const maxIndex = Math.max(0, visibleSlides.length - Math.floor(this.getColumns()));
    
    prevBtn.disabled = this.currentIndex <= 0;
    nextBtn.disabled = this.currentIndex >= maxIndex;
  }

  // ==========================================
  // OBSERVERS
  // ==========================================

  observeAll() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) {
      setTimeout(() => this.observeAll(), 500);
      return;
    }

    let wasOpen = false;
    let lastItemCount = -1;
    let syncTimer = null;

    const observer = new MutationObserver(() => {
      if (this.isUpdating) return;

      const isOpen = cartDrawer.classList.contains('active') || 
                     cartDrawer.classList.contains('animate');

      // Detectar apertura del drawer
      if (isOpen && !wasOpen) {
        wasOpen = true;
        lastItemCount = cartDrawer.querySelectorAll('.cart-item').length;
        console.log('📬 Drawer opened');
        // Al abrir, sincronizar sin skeleton (compara hash internamente)
        this.syncWithCart();
        return;
      } else if (!isOpen && wasOpen) {
        wasOpen = false;
        console.log('📪 Drawer closed');
        if (syncTimer) clearTimeout(syncTimer);
        return;
      }

      // Solo verificar cambios si el drawer está abierto
      if (!isOpen) return;

      // Detectar cambio en cantidad de items del DOM
      const currentItemCount = cartDrawer.querySelectorAll('.cart-item').length;
      if (currentItemCount !== lastItemCount) {
        lastItemCount = currentItemCount;
        console.log('🔄 DOM items changed:', currentItemCount);
        
        // Debounce para evitar múltiples llamadas
        if (syncTimer) clearTimeout(syncTimer);
        syncTimer = setTimeout(() => {
          this.syncWithCart();
        }, 100);
      }
    });

    observer.observe(cartDrawer, {
      attributes: true,
      attributeFilter: ['class'],
      childList: true,
      subtree: true
    });

    console.log('👀 Observer attached');
  }

  // ==========================================
  // EVENTOS
  // ==========================================

  bindEvents() {
    // Navegación
    document.addEventListener('click', (e) => {
      if (e.target.closest('.enova-crosssell-nav__btn--prev')) {
        e.preventDefault();
        this.goToSlide(this.currentIndex - 1);
      }
      if (e.target.closest('.enova-crosssell-nav__btn--next')) {
        e.preventDefault();
        this.goToSlide(this.currentIndex + 1);
      }
    });

    // Add to cart
    document.addEventListener('click', (e) => {
      const button = e.target.closest('.enova-crosssell-btn');
      if (!button || button.disabled || button.classList.contains('is-loading')) return;
      
      const form = button.closest('.enova-crosssell-form');
      if (!form) return;
      
      e.preventDefault();
      e.stopPropagation();
      this.handleAddToCart(form, button);
    }, true);

    // Variantes
    document.addEventListener('change', (e) => {
      if (e.target.classList.contains('enova-crosssell-variant-select')) {
        this.handleVariantChange(e.target);
      }
    });

    // Drag
    this.setupDrag();

    // Resize
    let resizeTimer = null;
    window.addEventListener('resize', () => {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => this.refresh(), 200);
    });

    console.log('🔗 Events bound');
  }

  setupDrag() {
    let startX = 0, currentX = 0, startTranslate = 0, isDragging = false;
    let viewport = null, track = null;

    const getTranslateX = () => {
      if (!track) return 0;
      const matrix = new DOMMatrix(getComputedStyle(track).transform);
      return matrix.m41;
    };

    const isInteractiveElement = (target) => {
      // No iniciar drag en elementos interactivos
      const interactive = target.closest('select, input, button, a, .enova-crosssell-btn, .enova-crosssell-variant-select');
      return !!interactive;
    };

    const onStart = (x, target) => {
      // Ignorar si es un elemento interactivo
      if (isInteractiveElement(target)) return false;
      
      viewport = target.closest('.enova-crosssell__viewport');
      if (!viewport) return false;
      track = viewport.querySelector('.enova-crosssell__track');
      if (!track) return false;
      
      startX = currentX = x;
      startTranslate = getTranslateX();
      isDragging = true;
      track.style.transition = 'none';
      return true;
    };

    const onMove = (x) => {
      if (!isDragging || !track) return;
      currentX = x;
      track.style.transform = `translateX(${startTranslate + (currentX - startX)}px)`;
    };

    const onEnd = () => {
      if (!isDragging) return;
      isDragging = false;
      if (track) track.style.transition = 'transform 0.3s ease';
      
      const diff = startX - currentX;
      if (Math.abs(diff) > 50) {
        this.goToSlide(this.currentIndex + (diff > 0 ? 1 : -1));
      } else {
        this.goToSlide(this.currentIndex);
      }
    };

    // Touch
    document.addEventListener('touchstart', (e) => {
      if (!isInteractiveElement(e.target)) {
        onStart(e.touches[0].clientX, e.target);
      }
    }, { passive: true });
    document.addEventListener('touchmove', (e) => onMove(e.touches[0].clientX), { passive: true });
    document.addEventListener('touchend', onEnd);

    // Mouse
    document.addEventListener('mousedown', (e) => {
      // Ignorar elementos interactivos
      if (isInteractiveElement(e.target)) return;
      if (e.button === 0 && onStart(e.clientX, e.target)) e.preventDefault();
    });
    document.addEventListener('mousemove', (e) => {
      if (isDragging) { e.preventDefault(); onMove(e.clientX); }
    });
    document.addEventListener('mouseup', onEnd);

    // Trackpad
    let lastWheel = 0;
    document.addEventListener('wheel', (e) => {
      if (!e.target.closest('.enova-crosssell__viewport')) return;
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY) && Math.abs(e.deltaX) > 15) {
        e.preventDefault();
        if (Date.now() - lastWheel < 400) return;
        lastWheel = Date.now();
        this.goToSlide(this.currentIndex + (e.deltaX > 0 ? 1 : -1));
      }
    }, { passive: false });
  }

  // ==========================================
  // ADD TO CART
  // ==========================================

  async handleAddToCart(form, button) {
    const variantId = form.querySelector('.variant-id')?.value;
    if (!variantId) return;

    button.classList.add('is-loading');
    button.disabled = true;

    try {
      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: parseInt(variantId), quantity: 1 }] })
      });

      if (!response.ok) throw new Error('Failed');

      button.classList.remove('is-loading');
      button.classList.add('is-success');

      // Actualizar UI
      await this.updateCartUI();

      setTimeout(() => {
        button.classList.remove('is-success');
        button.disabled = false;
      }, 2000);

    } catch (e) {
      console.error('❌', e);
      button.classList.remove('is-loading');
      button.disabled = false;
    }
  }

  async updateCartUI() {
    if (this.isUpdating) return;
    this.isUpdating = true;

    try {
      const cartDrawer = document.querySelector('cart-drawer');
      if (!cartDrawer) return;

      const response = await fetch(window.Shopify.routes.root + '?sections=cart-drawer,cart-icon-bubble');
      const sections = await response.json();

      const parser = new DOMParser();
      const doc = parser.parseFromString(sections['cart-drawer'], 'text/html');

      // Items
      const newItems = doc.querySelector('cart-drawer-items');
      const currentItems = cartDrawer.querySelector('cart-drawer-items');
      if (newItems && currentItems) currentItems.innerHTML = newItems.innerHTML;

      // Totales
      const newTotals = doc.querySelector('.totals');
      const currentTotals = cartDrawer.querySelector('.totals');
      if (newTotals && currentTotals) currentTotals.innerHTML = newTotals.innerHTML;

      // Empty state
      const newDrawer = doc.querySelector('cart-drawer');
      if (newDrawer) {
        cartDrawer.classList.toggle('is-empty', newDrawer.classList.contains('is-empty'));
      }

      // Bubble
      if (sections['cart-icon-bubble']) {
        const iconDoc = parser.parseFromString(sections['cart-icon-bubble'], 'text/html');
        const newBubble = iconDoc.querySelector('.cart-count-bubble');
        const currentBubble = document.querySelector('.cart-count-bubble');
        if (newBubble && currentBubble) currentBubble.innerHTML = newBubble.innerHTML;
      }

      // Sincronizar carrusel (el observer también puede detectarlo)
      await this.syncWithCart();

    } catch (e) {
      console.error('❌', e);
    } finally {
      this.isUpdating = false;
    }
  }

  handleVariantChange(select) {
    const item = select.closest('.enova-crosssell-item');
    if (!item) return;

    const option = select.options[select.selectedIndex];
    const price = parseInt(option.dataset.price || '0', 10);
    const comparePrice = parseInt(option.dataset.comparePrice || '0', 10);
    const available = option.dataset.available === 'true';

    const input = item.querySelector('.variant-id');
    if (input) input.value = select.value;

    const priceEl = item.querySelector('.enova-crosssell-item__price');
    if (priceEl && price > 0) {
      if (comparePrice > price) {
        priceEl.innerHTML = `
          <span class="enova-crosssell-item__price--sale">${this.formatMoney(price)}</span>
          <span class="enova-crosssell-item__price--compare">${this.formatMoney(comparePrice)}</span>
        `;
      } else {
        priceEl.innerHTML = `<span class="enova-crosssell-item__price--regular">${this.formatMoney(price)}</span>`;
      }
    }

    const btn = item.querySelector('.enova-crosssell-btn');
    if (btn) {
      btn.disabled = !available;
      btn.classList.toggle('enova-crosssell-btn--soldout', !available);
    }
  }

  formatMoney(cents) {
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents, window.theme?.moneyFormat || '${{amount}}');
    }
    return '$' + (cents / 100).toFixed(2);
  }
}

// Init
if (!window.enovaCrosssellCarousel) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.enovaCrosssellCarousel = new EnovaCrosssellCarousel();
    });
  } else {
    window.enovaCrosssellCarousel = new EnovaCrosssellCarousel();
  }
}

